import { createSelector } from 'reselect';
import { getIsModalOpen } from 'a/hub/selectors/modalSystemSelectors';
import { getFluencyAssignment, getFluencySuppressionArmed } from 'a/boostFluency/redux/selectors';
import {
  VOCAB_PROGRESS,
  ACTIVITY_STATUS,
  WORKSHOP_ONBOARDING_QUEST_IDS,
  WORKSHOP_BENCHMARK_IDS,
  WORKSHOP_BOOK_TRACK_IDS,
} from '../data/constants';
import STEP_TYPE from '../data/StepTypes';
import { normalFlows, wellBelowFlows, getWorkshopStepConfig } from '../data/workshopSteps';
import { getIndexFromActivityId } from '../lib/workshopUtility';

export const getChallengeQuestGames = createSelector(
  [(state) => state.workshopReducer.workshop.challengeQuest.games],
  (games = []) => games.filter(game => game.type === STEP_TYPE.GAME),
);

export const getIsChallengeQuestComplete = createSelector(
  [getChallengeQuestGames],
  (games = []) => games.length && games.every(({ levels, levelsMastered }) => levelsMastered.length >= levels.length),
);

export const getChallengeQuestGamesCompleteCount = createSelector(
  [getChallengeQuestGames],
  (games) => games.reduce((completeCount, game) => {
    const { levelsMastered, levels } = game;
    return levelsMastered.length >= levels.length ? completeCount + 1 : completeCount;
  }, 0),
);

export const getShouldStartNewChallengeQuest = state => state.workshopReducer.shouldStartNewChallengeQuest;

export const getCurrentQuestId = state =>
  state.workshopReducer.currentQuestId;

export const getLastQuestFinishedBeforeEndOfQuests = ({ workshopReducer: { lastQuestFinishedBeforeEndOfQuests } }) =>
  lastQuestFinishedBeforeEndOfQuests;

export const getCurrentQuestNumber = createSelector(
  [
    getCurrentQuestId,
    getLastQuestFinishedBeforeEndOfQuests,
  ],
  (activeQuestId, lastQuestFinishedBeforeEndOfQuests) => {
    const currentQuestNumber = parseInt(activeQuestId, 10);
    const lastQuestNumber = parseInt(lastQuestFinishedBeforeEndOfQuests, 10);
    const questName = (currentQuestNumber === 0 ? lastQuestNumber || 300 : currentQuestNumber) - 300;
    return questName;
  },
);

const defaultState = {
  eReader: { status: 'inactive' },
  vocab: { status: 'inactive' },
  TFE: { status: 'inactive' },
  battery: { status: 'inactive' },
  transmissionSequenceId: null,
  transmissionText: null,
};

export const getCurrentWorkshopQuest = ({ workshopReducer }) => {
  const {
    currentQuestId,
    lastQuestFinishedBeforeEndOfQuests,
    workshop,
  } = workshopReducer;

  if (currentQuestId !== 0) {
    return workshop[`quest${currentQuestId}`] || defaultState;
  }
  return workshop[`quest${lastQuestFinishedBeforeEndOfQuests}`] || defaultState;
};

export const getNextQuestId = (state) => {
  const { nextQuestId = 0 } = getCurrentWorkshopQuest(state);
  return nextQuestId;
};

export const getVocabSessionsCompleteCount = createSelector(
  [getCurrentWorkshopQuest],
  ({ vocab }) => vocab.sessions.filter(session => session.progress === VOCAB_PROGRESS.COMPLETE).length,
);

export const getAreAllQuestsAndChallengesComplete = (state) => {
  const { product } = state.appSessionReducer;
  return !!state.workshopReducer.areAllQuestsAndChallengesComplete[product];
};

export const getIsTransmitterInTransmissionState = createSelector(
  [getCurrentWorkshopQuest],
  ({ vocab }) => vocab.status === ACTIVITY_STATUS.TRANSMISSION,
);

export const getWorkshopActivities = ({ workshopReducer }) => {
  const { currentQuestId, workshop } = workshopReducer;
  let quest = workshop[`quest${currentQuestId}`] || defaultState;
  if (currentQuestId === 0) {
    quest = workshop.challengeQuest || defaultState;
  }
  const { eReader, vocab, TFE, battery } = quest;
  return {
    eReader,
    vocab,
    TFE,
    battery,
  };
};

export const getAreAllActivitiesFinished = (state) =>
  Object.values(getWorkshopActivities(state)).every(
    ({ status }) => [ACTIVITY_STATUS.FINISHED, ACTIVITY_STATUS.FINISHED_CALL_TO_ACTION].includes(status),
  );

export const getCurrentTransmissionText = createSelector(
  getCurrentWorkshopQuest,
  (quest = {}) => quest.transmissionText,
);

export const getCurrentTransmissionSequenceId = createSelector(
  getCurrentWorkshopQuest,
  (quest = {}) => quest.transmissionSequenceId,
);

export const getIsOnboardingQuest = createSelector(
  state => state.workshopReducer.currentQuestId,
  state => state.questWorkflowReducer.isQuestInDemoMode,
  (currentQuestId, isQuestInDemoMode) =>
    !isQuestInDemoMode && WORKSHOP_ONBOARDING_QUEST_IDS.includes(parseInt(currentQuestId, 10)),
);

export const getIsChallengeQuest = createSelector(
  state => state.selectedActivity.params.isChallengeQuest,
  state => state.workshopReducer.currentQuestId,
  (isLaunchedFromChallenge, currentQuestId) => isLaunchedFromChallenge || currentQuestId === 0,
);

export const getIsPracticeEnabledByTeacher = state => state.questWorkflowReducer.showPracticeTab;

export const getIsPracticeEnabled = createSelector(
  [getIsPracticeEnabledByTeacher, getAreAllQuestsAndChallengesComplete],
  (isPracticeEnabledByTeacher, areAllQuestsAndChallengesComplete) =>
    isPracticeEnabledByTeacher || areAllQuestsAndChallengesComplete,
);

export const getIsQuestInDemoMode = state => state.workshopReducer.isQuestInDemoMode;

export const getHasPracticeAccess = createSelector(
  [getIsPracticeEnabled, getIsOnboardingQuest, getIsQuestInDemoMode],
  (isPracticeEnabled, isOnboardingQuest, isQuestInDemoMode) =>
    (isPracticeEnabled && !isOnboardingQuest) || isQuestInDemoMode,
);

export const getShoweReaderRewardModal = createSelector(
  [
    getCurrentWorkshopQuest,
  ],
  (quest) => quest.eReader.status === ACTIVITY_STATUS.REWARD,
);

export const getShowTFERewardModal = createSelector(
  [
    getCurrentWorkshopQuest,
  ],
  (quest) => quest.TFE.status === ACTIVITY_STATUS.REWARD,
);

export const getIsRewardModalOpen = createSelector(
  [
    getShowTFERewardModal,
    getShoweReaderRewardModal,
  ],
  (isTFEOpen, isEreaderOpen) => isEreaderOpen || isTFEOpen);

export const getIsWorkshopModalOpen = createSelector(
  [
    getIsModalOpen,
    getCurrentTransmissionText,
    getIsRewardModalOpen,
  ],
  (isOpenFromModalSystem, transmissionText, isRewardModalOpen) =>
    isOpenFromModalSystem
      || !!transmissionText
      || isRewardModalOpen,
);

export const getShouldShowWorkshopMobile = createSelector(
  [
    getIsOnboardingQuest,
    getAreAllActivitiesFinished,
    getIsQuestInDemoMode,
  ],
  (
    isOnboardingQuest,
    areAllActivitiesFinished,
    isQuestInDemoMode,
  ) => !isOnboardingQuest || areAllActivitiesFinished || isQuestInDemoMode);

export const getSelectedCurioso = state => state.workshopReducer.selectedCurioso;

export const getSelectedCuriosoType = state => getSelectedCurioso(state)?.type;

export const getIsCuriosoSelectionComplete = state => !!getSelectedCuriosoType(state);

export const getWorkshopStep = state => state.workshopReducer.workshopStep;

export const getMyCollection = state => state.workshopReducer.myCollection;

export const getSelectedBookFromCollection = state => getMyCollection(state).selectedBook;

export const getWorkshopGames = (state) => {
  const isChallengeQuest = getIsChallengeQuest(state);

  if (isChallengeQuest) {
    return getChallengeQuestGames(state);
  }
  return getCurrentWorkshopQuest(state).games;
};

export const getHasSeenWorkshopOutro = (state) => {
  const { hasSeenWorkshopOutro } = state.workshopReducer;
  return hasSeenWorkshopOutro;
};

export const getWorkshopBenchmark = state => state.workshopReducer?.benchmark || 'NORMAL';

export const getWorkshopBookTrack = state =>
  state.workshopReducer?.bookTrack || WORKSHOP_BOOK_TRACK_IDS.NORMAL;

export const getIsWellBelowBenchmark = createSelector(
  [getWorkshopBenchmark],
  (benchmark) => benchmark === WORKSHOP_BENCHMARK_IDS.WELL_BELOW,
);

export const getGamesForDisplay = (state) => {
  const benchmark = getWorkshopBenchmark(state);

  let allGames = getCurrentWorkshopQuest(state).games;
  if (getIsChallengeQuest(state)) {
    allGames = getChallengeQuestGames(state);
  }

  if (benchmark === WORKSHOP_BENCHMARK_IDS.WELL_BELOW) {
    // well below benchamr diplays games in sets of 2, so we need to divide allGames into three arrays, with 2 games in each array.

    // first we sort the games by having mastered games at the beggining of the array
    const orderedGames = [...allGames].sort((a, b) => {
      const aMastered = a.levelsMastered.length >= a.levels.length;
      const bMastered = b.levelsMastered.length >= b.levels.length;
      if (aMastered === bMastered) {
        return 0;
      }
      return aMastered ? -1 : 1;
    });

    // then we ignore the pairs that are marked as completed, and return the first incomplete pair
    const games = [];
    for (let i = 0; i < orderedGames.length; i += 2) {
      const pair = orderedGames.slice(i, i + 2);
      if (pair.some(game => game.levelsMastered.length < game.levels.length)) {
        games.push(...pair);
        break;
      }
    }
    return games;
  }
  return allGames;
};

const getSelectedSkillId = state => state.selectedActivity.params.skillId;

export const getSelectedGame = (state) =>
  getWorkshopGames(state).find(game => getSelectedSkillId(state) === game.skillId);

export const getIsSelectedGameComplete = (state) => {
  const selectedGame = getSelectedGame(state);
  return selectedGame.levelsMastered.length >= selectedGame.levels.length;
};

export const getGameFailures = (state) => state.workshopReducer.gameFailures;

export const getGameFailuresByKey = (state, key) => getGameFailures(state)[key] || 0;

export const getIsGameFailedThreeTimes = (state, key) => getGameFailuresByKey(state, key) >= 3;

export const getTotalQuestGamesCount = createSelector(
  [getCurrentWorkshopQuest],
  quest => quest.games.length,
);

export const getMasteredGamesCount = (state) =>
  getCurrentWorkshopQuest(state).games.reduce((accu, current) =>
    (current.levels.length <= current.levelsMastered.length ? accu + 1 : accu), 0,
  );

export const getIsWorkshopIntroFinished = createSelector(
  [getIsOnboardingQuest, getWorkshopStep, getIsQuestInDemoMode],
  (isOnboardingQuest, workshopStep, isDemo) => !isOnboardingQuest || workshopStep > 0 || isDemo,
);

export const getCurrentStepConfig = (state) => {
  const step = getWorkshopStep(state);
  const isOnboardingQuest = getIsOnboardingQuest(state);
  const benchmarkId = getWorkshopBenchmark(state);
  const benchmarkFlow = benchmarkId === WORKSHOP_BENCHMARK_IDS.WELL_BELOW ? wellBelowFlows : normalFlows;

  if (getIsQuestInDemoMode(state)) {
    const [demoStep] = benchmarkFlow.demo;
    return demoStep;
  }

  if (getAreAllQuestsAndChallengesComplete(state)) {
    const [endOfEverythingStep] = benchmarkFlow.endOfEverything;
    return endOfEverythingStep;
  }

  if (getIsChallengeQuest(state)) {
    const [challengeStep] = benchmarkFlow.challenge;
    return challengeStep;
  }
  const flowConfig = isOnboardingQuest ? benchmarkFlow.onboarding : benchmarkFlow.standard;
  return flowConfig.find((config) => config.step === step);
};

export const getCurrentTFEEpisodeConfig = state => {
  const { TFE: { episode } } = getCurrentWorkshopQuest(state);
  const tfeConfig = state.tfeReducer.config;
  const episodeConfig = tfeConfig.find(({ id, available }) => id === episode.id && available);
  return episodeConfig;
};

export const getTFEPartText = (state) => {
  const { targetActivityId } = getCurrentStepConfig(state);
  if (!targetActivityId) {
    return '';
  }
  const index = getIndexFromActivityId(targetActivityId);
  return `Part ${index + 1}`;
};

export const getCurrentTFEEpisodePath = state => {
  const episodeConfig = getCurrentTFEEpisodeConfig(state);
  // if the benchmark is normal, return the episode path
  const benchmarkId = getWorkshopBenchmark(state);
  if (benchmarkId === WORKSHOP_BENCHMARK_IDS.NORMAL) {
    return episodeConfig?.path;
  }
  // if the benchmark is well below, return the path of the part that corresponds to the current activity part
  const { TFE: { episode: { parts } } } = getCurrentWorkshopQuest(state);
  const { targetActivityId } = getCurrentStepConfig(state);
  const index = getIndexFromActivityId(targetActivityId);
  const { path } = episodeConfig?.partsPathByLevel?.rebel?.find(({ id }) => id === parts[index]?.id) ?? {};
  return path;
};

export const getVocabTransmissionKey = (state) => {
  const { transmissionIndex = 1 } = getCurrentStepConfig(state) || {};
  const questId = getCurrentQuestId(state);
  const vocabTransmissionKey = `${questId}-${transmissionIndex}`;
  return vocabTransmissionKey;
};

export const getTargetActivityId = createSelector(
  [getCurrentStepConfig],
  (config) => config.targetActivityId,
);

// Returns the workshop step config for `currentStep - 1`, scoped to onboarding flows.
// Returns undefined when there is no previous step (e.g. step 0) or when the quest
// isn't on an onboarding flow. The fluency suppression rule only fires on onboarding
// quests, so we don't need to handle demo / endOfEverything / challenge branches —
// onboarding quests are mutually exclusive with all of them.
export const getPreviousStepConfig = createSelector(
  [getWorkshopStep, getWorkshopBenchmark, getIsOnboardingQuest],
  (step, benchmarkId, isOnboardingQuest) => {
    if (!isOnboardingQuest || step <= 0) {
      return undefined;
    }
    return getWorkshopStepConfig(step - 1, benchmarkId, true);
  },
);

// Transmission narrations that explicitly prompt the student toward a specific
// next action. When the suppression flag is armed (see `getIsFluencyStepActive`)
// and the previous step played one of these, we hide the fluency CTA so we
// don't contradict the instructions the student just heard. These IDs only
// exist in the onboarding flows, so no separate onboarding gate is needed.
const FLUENCY_SUPPRESSING_TRANSMISSIONS = ['intro', 'secondGameMastered', '1-onboarding'];

// True only when a fluency assignment exists AND the current workshop step has a
// targetActivityId — matches the guard in applyFluencyStepConfig so the click
// handler and the action can't disagree on whether the fluency step is active.
// The previous-step transmission rule only fires when the suppression flag is
// armed, which happens exclusively via `advanceToNextWorkshopStep` calling
// `applyWorkshopStepConfig(true)`. Entry-point callers (mount, startWorkshop,
// openNextQuest) use the default `applyWorkshopStepConfig()` which leaves the
// flag disarmed, so the rule is skipped on login / quest transition — the
// narration played in a previous session isn't contextually relevant anymore.
export const getIsFluencyStepActive = createSelector(
  [getFluencyAssignment, getTargetActivityId, getFluencySuppressionArmed, getPreviousStepConfig],
  (assignment, targetActivityId, isArmed, prevStepConfig) => {
    if (!assignment || !targetActivityId) {
      return false;
    }
    if (!isArmed) {
      return true;
    }
    const prevTransmissionId = prevStepConfig?.transmissionSequenceId;
    if (
      prevTransmissionId
      && FLUENCY_SUPPRESSING_TRANSMISSIONS.some((id) => prevTransmissionId.includes(id))
    ) {
      return false;
    }
    return true;
  },
);

export const getIsWorkshopStepInProgress = (state) => {
  const { isStepInProgress } = state.workshopReducer;
  return isStepInProgress;
};

export const getWorkshopUIConfig = createSelector(
  getMyCollection,
  ({
    selectedCurtains,
    selectedBook,
    selectedDesk,
    selectedMusic,
    selectedColor,
    selectedCollectionsTab = 'curtains',
  }) => ({
    selectedCurtains,
    selectedBook,
    selectedDesk,
    selectedMusic,
    selectedColor,
    selectedCollectionsTab,
  }),
);
